import { History, KeyRound, Lock, MoreHorizontal, Unlock } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "@/components/ui/use-toast";
import { useConfirmationAlert } from "@/hook/confirmation-alert/hooks/actions";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import {
  IStoreOperatorRecord,
  useLazyGetAuditHistoryQuery,
  useResetOperatorPasswordMutation,
  useToggleOperatorBlockMutation,
} from "@/services/store-access-query";

import { extractErrorMessage } from "../../hooks/utils";
import { AuditHistoryContent } from "../row-actions/audit-history-content";

interface OperatorRowActionsProps {
  record: IStoreOperatorRecord;
  storeCode: number;
}

type OperatorAction = "block" | "unblock" | "resetPassword";

export const OperatorRowActions: React.FC<OperatorRowActionsProps> = ({
  record,
  storeCode,
}) => {
  const [toggleOperatorBlock, { isLoading: isToggling }] =
    useToggleOperatorBlockMutation();
  const [resetPassword, { isLoading: isResetting }] =
    useResetOperatorPasswordMutation();
  const [getAuditHistory] = useLazyGetAuditHistoryQuery();
  const { handleAlert } = useConfirmationAlert();
  const { handleModal } = useDialogModal();

  const isLoading = isToggling || isResetting;

  const getModalConfig = (action: OperatorAction) => {
    const name = record.operatorName || record.operatorCode;
    if (action === "block") {
      return {
        title: "Bloquear Operador",
        description: `Tem certeza que deseja bloquear o operador "${name}"?`,
        confirmText: "Bloquear",
      };
    }
    if (action === "unblock") {
      return {
        title: "Desbloquear Operador",
        description: `Tem certeza que deseja desbloquear o operador "${name}"?`,
        confirmText: "Desbloquear",
      };
    }
    return {
      title: "Resetar Senha",
      description: `A senha do operador "${name}" será resetada para: 12345. Deseja continuar?`,
      confirmText: "Resetar Senha",
    };
  };

  const executeAction = async (action: OperatorAction) => {
    if (action === "block") {
      await toggleOperatorBlock({
        storeCode,
        operatorCode: record.operatorCode,
        blocked: true,
      }).unwrap();
      toast({
        title: "Sucesso",
        description: "Operador bloqueado com sucesso",
      });
      return;
    }
    if (action === "unblock") {
      await toggleOperatorBlock({
        storeCode,
        operatorCode: record.operatorCode,
        blocked: false,
      }).unwrap();
      toast({
        title: "Sucesso",
        description: "Operador desbloqueado com sucesso",
      });
      return;
    }
    const result = await resetPassword({
      storeCode,
      operatorCode: record.operatorCode,
    }).unwrap();
    toast({
      title: "Sucesso",
      description: result.message || "Senha resetada com sucesso",
    });
  };

  const handleAction = (action: OperatorAction) => {
    const config = getModalConfig(action);
    handleAlert({
      isOpen: true,
      title: config.title,
      description: config.description,
      confirmText: config.confirmText,
      isLoading: false,
      onConfirm: async () => {
        try {
          await executeAction(action);
        } catch (error) {
          toast({
            title: "Erro",
            description:
              extractErrorMessage(error) ||
              "Erro ao executar ação. Tente novamente.",
            variant: "destructive",
          });
          console.error("Erro na ação de operador:", error);
          throw error;
        }
      },
    });
  };

  const handleViewHistory = async () => {
    const entityId = `${storeCode}-${record.operatorCode.trim()}`;
    try {
      const result = await getAuditHistory([entityId]).unwrap();
      handleModal({
        isOpen: true,
        title: "Histórico de Ações",
        description: `Histórico de ações do operador: ${record.operatorName || record.operatorCode}`,
        element: <AuditHistoryContent data={result} />,
      });
    } catch (error) {
      toast({
        title: "Erro",
        description:
          extractErrorMessage(error) || "Erro ao buscar histórico.",
        variant: "destructive",
      });
      console.error("Erro ao buscar histórico:", error);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          aria-label="Abrir ações do operador"
        >
          <span className="sr-only">Abrir menu</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {record.blocked ? (
          <DropdownMenuItem
            onClick={() => handleAction("unblock")}
            disabled={isLoading}
          >
            <Unlock className="mr-2 h-4 w-4" />
            Desbloquear Operador
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            onClick={() => handleAction("block")}
            disabled={isLoading}
          >
            <Lock className="mr-2 h-4 w-4" />
            Bloquear Operador
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onClick={() => handleAction("resetPassword")}
          disabled={isLoading}
        >
          <KeyRound className="mr-2 h-4 w-4" />
          Resetar Senha
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleViewHistory}>
          <History className="mr-2 h-4 w-4" />
          Ver Histórico
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
